import React from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { UserPlus, User, Shield, ArrowRight } from 'lucide-react';
import { USER_TYPES } from './assignationsShared';

export const AssignationCreateModal = ({ open, onOpenChange, formData, setFormData, admins, availableUsers, onCreate }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-indigo-600" />
              Nouvelle Assignation
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Sélection Admin */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-orange-600" />
                Administrateur *
              </Label>
              <Select 
                value={formData.admin_id} 
                onValueChange={(v) => setFormData({...formData, admin_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un administrateur" />
                </SelectTrigger>
                <SelectContent>
                  {admins.map(admin => (
                    <SelectItem key={admin.id} value={admin.id}>
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-orange-600" />
                        {admin.prenom} {admin.nom}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Type d'utilisateur */}
            <div>
              <Label className="mb-2 block">Type d&apos;utilisateur *</Label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(USER_TYPES).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setFormData({...formData, assigned_user_type: key, assigned_user_id: ''})}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.assigned_user_type === key 
                          ? `${config.bgLight} border-current ${config.textColor}` 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className={`h-6 w-6 mx-auto mb-1 ${formData.assigned_user_type === key ? config.textColor : 'text-gray-400'}`} />
                      <span className="text-sm font-medium">{config.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Sélection Utilisateur */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                {React.createElement(USER_TYPES[formData.assigned_user_type]?.icon || User, { 
                  className: `h-4 w-4 ${USER_TYPES[formData.assigned_user_type]?.textColor || 'text-gray-600'}` 
                })}
                Utilisateur à assigner *
              </Label>
              <Select 
                value={formData.assigned_user_id} 
                onValueChange={(v) => setFormData({...formData, assigned_user_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder={`Sélectionner un ${USER_TYPES[formData.assigned_user_type]?.label || 'utilisateur'}`} />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.length === 0 ? (
                    <div className="p-3 text-center text-gray-500 text-sm">
                      Aucun utilisateur disponible
                    </div>
                  ) : (
                    availableUsers.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        <div>
                          <p className="font-medium">{user.name}</p>
                          {user.email && <p className="text-xs text-gray-500">{user.email}</p>}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            
            {/* Aperçu */}
            {formData.admin_id && formData.assigned_user_id && (
              <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <div className="flex items-center justify-center gap-4">
                  <div className="text-center">
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-1">
                      <Shield className="h-5 w-5 text-orange-600" />
                    </div>
                    <p className="text-xs font-medium">
                      {admins.find(a => a.id === formData.admin_id)?.prenom}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-indigo-400" />
                  <div className="text-center">
                    <div className={`w-10 h-10 rounded-full ${USER_TYPES[formData.assigned_user_type]?.bgLight} flex items-center justify-center mx-auto mb-1`}>
                      {React.createElement(USER_TYPES[formData.assigned_user_type]?.icon || User, { 
                        className: `h-5 w-5 ${USER_TYPES[formData.assigned_user_type]?.textColor}` 
                      })}
                    </div>
                    <p className="text-xs font-medium">
                      {availableUsers.find(u => u.id === formData.assigned_user_id)?.name?.split(' ')[0]}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button 
              onClick={onCreate}
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={!formData.admin_id || !formData.assigned_user_id}
            >
              <UserPlus className="h-4 w-4 mr-1" />
              Créer l&apos;assignation
            </Button>
          </DialogFooter>
        </DialogContent>
    </Dialog>
  );
};
